import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import ScrollToTop from './components/ScrollToTop';
import SectionLoader from './components/SectionLoader';
import AnalyticsTracker from './components/AnalyticsTracker';
import ProtectedRoute from './components/ProtectedRoute';

// Lazy load pages for performance
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail'));
const Contact = lazy(() => import('./pages/Contact'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const OpenCompanySaudiArabiaCostForeignInvestment = lazy(() => import('./pages/OpenCompanySaudiArabiaCostForeignInvestment'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Terms = lazy(() => import('./pages/Terms'));
const CompanyFormationSaudi = lazy(() => import('./pages/CompanyFormationSaudi'));
const CompanySetupSaudi = lazy(() => import('./pages/CompanySetupSaudi'));
const ForeignInvestorSetup = lazy(() => import('./pages/ForeignInvestorSetup'));
const ForeignInvestmentService = lazy(() => import('./pages/ForeignInvestmentService'));
const CommercialRegistrationSaudi = lazy(() => import('./pages/CommercialRegistrationSaudi'));
const CommercialRegistrationRenewalSaudi = lazy(() => import('./pages/CommercialRegistrationRenewalSaudi'));
const CommercialRegistrationAmendmentSaudi = lazy(() => import('./pages/CommercialRegistrationAmendmentSaudi'));
const CommercialLicensesSaudi = lazy(() => import('./pages/CommercialLicensesSaudi'));
const BusinessSetupSaudi = lazy(() => import('./pages/BusinessSetupSaudi'));
const BusinessSetupLanding = lazy(() => import('./pages/BusinessSetupLanding'));
const ForeignBusinessSetupConsultingSaudi = lazy(() => import('./pages/ForeignBusinessSetupConsultingSaudi'));
const RiyadhBusinessSetup = lazy(() => import('./pages/RiyadhBusinessSetup'));
const JeddahBusinessSetup = lazy(() => import('./pages/JeddahBusinessSetup'));
const DammamBusinessSetup = lazy(() => import('./pages/DammamBusinessSetup'));
const KhobarBusinessSetup = lazy(() => import('./pages/KhobarBusinessSetup'));
const GovernmentLicensesManagementSaudi = lazy(() => import('./pages/GovernmentLicensesManagementSaudi'));
const MisaLicenseSaudi = lazy(() => import('./pages/MisaLicenseSaudi'));
const ContractorClassificationSaudi = lazy(() => import('./pages/ContractorClassificationSaudi'));
const LlcSetupSaudi = lazy(() => import('./pages/LlcSetupSaudi'));
const CommercialRegistrationCancellationSaudi = lazy(() => import('./pages/CommercialRegistrationCancellationSaudi'));
const QiwaMuqeemMadadServices = lazy(() => import('./pages/QiwaMuqeemMadadServices'));
const ZatcaServicesSaudi = lazy(() => import('./pages/ZatcaServicesSaudi'));
const WageProtectionAndInternalRegulationsSaudi = lazy(() => import('./pages/WageProtectionAndInternalRegulationsSaudi'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AnalyticsDashboard = lazy(() => import('./pages/admin/AnalyticsDashboard'));
const LeadsManager = lazy(() => import('./pages/admin/LeadsManager'));
const ServicesManager = lazy(() => import('./pages/admin/ServicesManager'));
const BlogManager = lazy(() => import('./pages/admin/BlogManager'));
const AiContentAssistant = lazy(() => import('./pages/admin/AiContentAssistant'));
const SearchPerformance = lazy(() => import('./pages/admin/SearchPerformance'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const ReadinessLeads = lazy(() => import('./pages/admin/ReadinessLeads'));
const UserDashboard = lazy(() => import('./pages/UserDashboard'));
const UserLogin = lazy(() => import('./pages/UserLogin'));
const Register = lazy(() => import('./pages/Register'));
const ThankYou = lazy(() => import('./pages/ThankYou'));
const NotFound = lazy(() => import('./pages/NotFound'));
const EntrepreneurSupport = lazy(() => import('./pages/EntrepreneurSupport'));
const CompanySetupReadinessAssessment = lazy(() => import('./pages/CompanySetupReadinessAssessment'));

export default function App() {
  return (
    <Router>
      <AnalyticsTracker />
      <ScrollToTop />
      <Routes>
        {/* Admin Routes - No Layout */}
        <Route path="/admin/login" element={<Suspense fallback={null}><AdminLogin /></Suspense>} />
        
        {/* Protected Admin Routes */}
        <Route path="/admin/dashboard" element={<ProtectedRoute><Suspense fallback={null}><AdminDashboard /></Suspense></ProtectedRoute>} />
        
        {/* User Routes */}
        <Route path="/login" element={<Suspense fallback={null}><UserLogin /></Suspense>} />
        <Route path="/register" element={<Suspense fallback={null}><Register /></Suspense>} />
        <Route path="/dashboard" element={<ProtectedRoute><Suspense fallback={null}><UserDashboard /></Suspense></ProtectedRoute>} />
        <Route path="/dashboard/*" element={<ProtectedRoute><Suspense fallback={null}><UserDashboard /></Suspense></ProtectedRoute>} />
        
        <Route path="/admin/analytics" element={<ProtectedRoute><Suspense fallback={null}><AnalyticsDashboard /></Suspense></ProtectedRoute>} />
        <Route path="/admin/leads" element={<ProtectedRoute><Suspense fallback={null}><LeadsManager /></Suspense></ProtectedRoute>} />
        <Route path="/admin/readiness-leads" element={<ProtectedRoute><Suspense fallback={null}><ReadinessLeads /></Suspense></ProtectedRoute>} />
        <Route path="/admin/services" element={<ProtectedRoute><Suspense fallback={null}><ServicesManager /></Suspense></ProtectedRoute>} />
        <Route path="/admin/blog" element={<ProtectedRoute><Suspense fallback={null}><BlogManager /></Suspense></ProtectedRoute>} />
        <Route path="/admin/ai-assistant" element={<ProtectedRoute><Suspense fallback={null}><AiContentAssistant /></Suspense></ProtectedRoute>} />
        <Route path="/admin/search-performance" element={<ProtectedRoute><Suspense fallback={null}><SearchPerformance /></Suspense></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute><Suspense fallback={null}><AdminSettings /></Suspense></ProtectedRoute>} />

        {/* Public Routes with Main Layout */}
        <Route path="*" element={
          <Layout>
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><SectionLoader /></div>}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/services/foreign-investment" element={<Navigate to="/foreign-investor-setup" replace />} />
                <Route path="/services/:slug" element={<ServiceDetail />} />
                <Route path="/company-setup-saudi-arabia" element={<CompanySetupSaudi />} />
                <Route path="/foreign-investor-setup" element={<ForeignInvestorSetup />} />
                <Route path="/company-formation-saudi-arabia" element={<CompanyFormationSaudi />} />
                <Route path="/commercial-registration-saudi-arabia" element={<CommercialRegistrationSaudi />} />
                <Route path="/commercial-registration-renewal-saudi-arabia" element={<CommercialRegistrationRenewalSaudi />} />
                <Route path="/commercial-registration-amendment-saudi-arabia" element={<CommercialRegistrationAmendmentSaudi />} />
                <Route path="/commercial-licenses-saudi-arabia" element={<CommercialLicensesSaudi />} />
                <Route path="/business-setup-saudi-arabia" element={<BusinessSetupSaudi />} />
                <Route path="/business-setup-consulting-saudi-arabia" element={<BusinessSetupLanding />} />
                <Route path="/foreign-business-setup-consulting-saudi-arabia" element={<ForeignBusinessSetupConsultingSaudi />} />
                <Route path="/business-setup-riyadh" element={<RiyadhBusinessSetup />} />
                <Route path="/business-setup-jeddah" element={<JeddahBusinessSetup />} />
                <Route path="/business-setup-dammam" element={<DammamBusinessSetup />} />
                <Route path="/business-setup-khobar" element={<KhobarBusinessSetup />} />
                <Route path="/government-licenses-management-saudi-arabia" element={<GovernmentLicensesManagementSaudi />} />
                <Route path="/misa-license-saudi-arabia" element={<MisaLicenseSaudi />} />
                <Route path="/contractor-classification-saudi-arabia" element={<ContractorClassificationSaudi />} />
                <Route path="/llc-setup-saudi-arabia" element={<LlcSetupSaudi />} />
                <Route path="/commercial-registration-cancellation-saudi-arabia" element={<CommercialRegistrationCancellationSaudi />} />
                <Route path="/qiwa-muqeem-madad-services-saudi-arabia" element={<QiwaMuqeemMadadServices />} />
                <Route path="/zatca-services-saudi-arabia" element={<ZatcaServicesSaudi />} />
                <Route path="/wage-protection-and-internal-regulations-saudi-arabia" element={<WageProtectionAndInternalRegulationsSaudi />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/open-company-saudi-arabia-cost-foreign-investment" element={<OpenCompanySaudiArabiaCostForeignInvestment />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/entrepreneur-support" element={<EntrepreneurSupport />} />
                <Route path="/company-setup-readiness-assessment" element={<CompanySetupReadinessAssessment />} />
                <Route path="/thank-you" element={<ThankYou />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </Layout>
        } />
      </Routes>
    </Router>
  );
}
